import React, { Fragment, memo } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { SideNavigation, NavigationContent } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';

import { APP_NAV_CONTAINER_EXPERIENCE, ExperienceSuccess } from '@confluence/experience-tracker';
import { AnonymousAccessSideNavMessage } from '@confluence/anonymous-access/entry-points/AnonymousAccessSideNavMessage';
import { PersistentInvitePeopleButton } from '@confluence/persistent-invite-button';
import { LoadableLazy, LoadableAfterPaint } from '@confluence/loadable';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import { PostOfficeConfluenceSideNavPlacement } from '@confluence/experiment-post-office-side-nav';

import { CommonSpaceNavigationContent } from './CommonSpaceNavigationContent';
import { SpaceHeader } from './Nav3SpaceHeader';
import { SpaceHeaderSkeleton } from './SpaceHeaderSkeleton';
import { LegacySpaceLinks } from './LegacySpaceLinks';
import { ContainerSkeleton } from './ContainerSkeleton';
import { SPACE_APPS_METRIC } from './perf.config';

const ErrorView = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ErrorView" */ './ErrorView')).ErrorView,
});

const Nav3SpaceApps = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-Nav3SpaceApps" */ './Nav3SpaceApps'))
			.Nav3SpaceApps,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegacySpaceLinksContainer = styled.ul({
	margin: token('space.0'),
	padding: token('space.0'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '18px',
	paddingTop: token('space.025'),
	listStyle: 'none',
	//eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > li': {
		marginTop: '0',
	},
});

const SidebarContent = memo(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled.div({
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBottom: '26px',
		position: 'relative',
		marginTop: token('space.0'),
	}),
);

export const Nav3SpaceNavigationContent = ({
	navigationProps,
	errorProps,
	name,
	iconPath,
	containsExternalCollaborators,
	isUserExternalCollaborator,
	isSpaceSettingsScreen,
	isUserViewingSpaceAsAnon,
}) => {
	const {
		loading,
		hasSpace,
		space,
		spaceKey,
		homepageId,
		main,
		quick,
		isSpaceAdmin,
		isBlogNavigation,
		blogsHidden,
		pageTreeFinishedLoading,
		onDragDropSuccess,
		showShortcutsSection,
		shouldRenderSpaceApps,
		allAppLinks,
		allVisibleAppLinks,
		loadingForgeApps,
		anonymousAccessQueryLoading,
		onContentTreeLoadComplete,
	} = navigationProps;

	const { unhandledError, hasRestrictedError, hasNotFoundError, anonymousAccessQueryError } =
		errorProps;

	const intl = useIntl();

	if (
		unhandledError ||
		hasRestrictedError ||
		hasNotFoundError ||
		anonymousAccessQueryError ||
		(!loading && !hasSpace)
	) {
		return (
			<ErrorView
				unhandledError={unhandledError || anonymousAccessQueryError}
				hasRestrictedError={hasRestrictedError}
				hasNotFoundError={hasNotFoundError}
			/>
		);
	}

	return (
		<SideNavigation label={intl.formatMessage(i18n.navRegionLabel)}>
			{loading || !name ? (
				<SpaceHeaderSkeleton />
			) : (
				<SpaceHeader
					spaceKey={spaceKey}
					spaceName={name}
					space={space}
					iconPath={iconPath}
					homepageId={homepageId}
					containsExternalCollaborators={containsExternalCollaborators}
					isUserExternalCollaborator={isUserExternalCollaborator}
				/>
			)}

			<NavigationContent>
				<SidebarContent data-vc="space-navigation-sidebar-content">
					{!loading ? <ExperienceSuccess name={APP_NAV_CONTAINER_EXPERIENCE} /> : <Fragment />}
					<LegacySpaceLinksContainer data-testId="legacy-space-links" data-vc="legacy-space-links">
						{loading ? (
							<ContainerSkeleton header={false} />
						) : (
							<LegacySpaceLinks
								isSpaceAdmin={isSpaceAdmin}
								links={main}
								isSpaceSettingsScreen={isSpaceSettingsScreen}
							/>
						)}
					</LegacySpaceLinksContainer>

					<CommonSpaceNavigationContent
						showShortcutsSection={showShortcutsSection}
						space={space}
						spaceKey={spaceKey}
						quick={quick}
						homepageId={homepageId}
						loading={loading}
						isSpaceAdmin={isSpaceAdmin}
						isBlogNavigation={isBlogNavigation}
						blogsHidden={blogsHidden}
						pageTreeFinishedLoading={pageTreeFinishedLoading}
						onDragDropSuccess={onDragDropSuccess}
						onContentTreeLoadComplete={onContentTreeLoadComplete}
					/>

					{!loading && !loadingForgeApps && shouldRenderSpaceApps ? (
						<ErrorBoundary attribution={Attribution.ECOSYSTEM}>
							<PageSegmentLoadStart metric={SPACE_APPS_METRIC} />
							<Nav3SpaceApps
								spaceKey={spaceKey}
								isSpaceAdmin={isSpaceAdmin}
								allAppLinks={allAppLinks}
								allVisibleAppLinks={allVisibleAppLinks}
							/>
						</ErrorBoundary>
					) : (
						<Fragment />
					)}
				</SidebarContent>
			</NavigationContent>

			<PostOfficeConfluenceSideNavPlacement />

			{isUserViewingSpaceAsAnon ? (
				<AnonymousAccessSideNavMessage />
			) : (
				<>{!anonymousAccessQueryLoading && <PersistentInvitePeopleButton source="pageTree" />}</>
			)}
		</SideNavigation>
	);
};

const i18n = defineMessages({
	navRegionLabel: {
		id: 'side-navigation.space-navigation.nav.region.aria.label',
		defaultMessage: 'Space',
		description: 'A label for the left-side navigation region',
	},
});
